/* eslint-disable max-lines-per-function */
import { mergeWith } from 'lodash-es';
import { assertEvent, assign } from 'xstate';

import { machine } from './machine';

const merge = <OBJ, SRC1, SRC2>(object: OBJ, source1: SRC1, source2: SRC2): OBJ & SRC1 & SRC2 => {
	return mergeWith(object, source1, source2, (_objValue, srcValue, key) =>
		key === 'recommendations' && Array.isArray(srcValue) ? srcValue : undefined,
	);
};

// this will be used to provide the implementation with external dependencies
export const createLogic = () =>
	machine.provide({
		actions: {
			onSubmit: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					// Use mergeWith to replace the recommendations array instead of concatenating
					// This ensures new recommendations overwrite old ones
					const draft = merge({}, context.draft, event.draft);

					return draft;
				},
			}),
			onError: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'error');

					const currentDraft = context.draft;

					const { issuePrompt = '', personalStory = '' } = currentDraft?.input ?? {};
					const description = `${issuePrompt} <br /><br />${personalStory}`;

					const userDraft = {
						type: 'user_draft',
						petitionDraft: { ask: null, description, image: null },
						recommendedDraft: null,
						metadata: { imageJobId: null },
					};

					return merge({}, context.draft, userDraft);
				},
			}),
			unsetDraftType: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'back');

					const targetBranch = event.target; // target = null

					if (targetBranch === null) {
						return { ...context.draft, type: null };
					}

					return context.draft;
				},
			}),
			unsetRecommendationsDraftType: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'back');

					const draftType = context.draft?.type; // 'recommendations_draft' | 'recommendations_variant_b_draft'

					if (draftType === 'recommendations_draft_ai' || draftType === 'recommendations_draft_user') {
						return { ...context.draft, type: 'recommendations_draft' };
					}

					if (
						draftType === 'recommendations_variant_b_draft_ai' ||
						draftType === 'recommendations_variant_b_draft_user'
					) {
						return { ...context.draft, type: 'recommendations_variant_b_draft' };
					}
					return context.draft;
				},
			}),
			persistDraftType: assign({
				// Updates the draft in context *after* the submit event for forward navigation.
				// Sets the correct type based on the experiment variant and the target branch.
				// eslint-disable-next-line complexity, max-statements
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					const baseDraft = merge({}, context.draft, event.draft);

					const experiment = context.draft?.metadata?.variant; // 'control' | 'recommendations' | 'recommendations_variant_b';
					const targetBranch = event.target; // 'ai' | 'user'

					if (targetBranch === null) {
						return { ...baseDraft, type: null };
					}

					if (experiment === 'control' && targetBranch === 'ai') {
						return { ...baseDraft, type: 'ai_draft' };
					}

					if (experiment === 'control' && targetBranch === 'user') {
						return { ...baseDraft, type: 'user_draft' };
					}

					if (experiment === 'recommendations' && targetBranch === 'ai') {
						return { ...baseDraft, type: 'recommendations_draft' };
					}

					if (experiment === 'recommendations' && targetBranch === 'user') {
						return { ...baseDraft, type: 'user_draft' };
					}

					if (experiment === 'recommendations_variant_b' && targetBranch === 'ai') {
						return { ...baseDraft, type: 'recommendations_variant_b_draft' };
					}

					if (experiment === 'recommendations_variant_b' && targetBranch === 'user') {
						return { ...baseDraft, type: 'user_draft' };
					}

					return baseDraft;
				},
			}),
			persistRecommendationsDraftType: assign({
				// Updates the draft in context *after* the submit event for forward navigation.
				// Sets the correct type based on the experiment variant and the target branch.
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					const baseDraft = merge({}, context.draft, event.draft);

					const draftType = context.draft?.type; // 'recommendations_draft' | 'recommendations_variant_b_draft'
					const targetBranch = event.target; // 'ai' | 'user'

					if (draftType === 'recommendations_draft' && targetBranch === 'ai') {
						return { ...baseDraft, type: 'recommendations_draft_ai' };
					}

					if (draftType === 'recommendations_draft' && targetBranch === 'user') {
						return { ...baseDraft, type: 'recommendations_draft_user' };
					}

					if (draftType === 'recommendations_variant_b_draft' && targetBranch === 'ai') {
						return { ...baseDraft, type: 'recommendations_variant_b_draft_ai' };
					}

					if (draftType === 'recommendations_variant_b_draft' && targetBranch === 'user') {
						return { ...baseDraft, type: 'recommendations_variant_b_draft_user' };
					}

					// TODO: Handle other experiments here as needed
					// add checks for recommendations_variant_b_draft_ai, recommendations_variant_b_draft_user

					return baseDraft;
				},
			}),
		},
		guards: {
			transitionTo: ({ event }, params) => {
				assertEvent(event, 'submit');

				return event.target === params.target;
			},
			// -- branching and transition point
			isUserDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft?.type === 'user_draft';
			},
			isAiDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft?.type === 'ai_draft';
			},
			// -- transition
			isRecommendationsDraft: ({ context }) => {
				if (context.draft?.metadata?.variant !== 'recommendations') {
					return false;
				}
				return context.draft.type === 'recommendations_draft';
			},
			// -- branching and transition point
			isRecommendationsAiDraft: ({ context, event }) => {
				if (context.draft?.metadata?.variant !== 'recommendations') {
					return false;
				}
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft.type === 'recommendations_draft_ai';
			},
			isRecommendationsUserDraft: ({ context, event }) => {
				if (context.draft?.metadata?.variant !== 'recommendations') {
					return false;
				}
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft.type === 'recommendations_draft_user';
			},
			// -- transition
			isRecommendationsVariantBDraft: ({ context }) => {
				if (context.draft?.metadata?.variant !== 'recommendations_variant_b') {
					return false;
				}
				return context.draft.type === 'recommendations_variant_b_draft';
			},
			// -- branching and transition point
			isRecommendationsVariantBAiDraft: ({ context, event }) => {
				if (context.draft?.metadata?.variant !== 'recommendations_variant_b') {
					return false;
				}
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft.type === 'recommendations_variant_b_draft_ai';
			},
			isRecommendationsVariantBUserDraft: ({ context, event }) => {
				if (context.draft?.metadata?.variant !== 'recommendations_variant_b') {
					return false;
				}
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft.type === 'recommendations_variant_b_draft_user';
			},
		},
	});
